import { useState, useEffect } from 'react'

interface ScaryLoaderProps {
  message?: string
}

export function ScaryLoader({ message = "Analyzing intelligence data..." }: ScaryLoaderProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [progress, setProgress] = useState(0)

  const steps = [
    "🔍 Fingerprinting target...",
    "🌐 Scanning darkweb databases...",
    "💀 Accessing hacker bases...",
    "🔐 Decrypting encrypted data...",
    "📡 Intercepting communications...",
    "🎯 Building target profile...",
    "⚡ Finalizing intelligence report..."
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval)
          return 100
        }
        return prev + Math.random() * 15
      })
    }, 200)

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length)
    }, 1500)

    return () => {
      clearInterval(interval)
      clearInterval(stepInterval)
    }
  }, [])

  return (
    <div className="min-h-screen bg-black text-red-500 flex items-center justify-center relative overflow-hidden">
      {/* Matrix-style background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/20 to-transparent"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute text-green-500 text-xs font-mono animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </div>
          ))}
        </div>
      </div>

      {/* Scanning lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1 bg-red-500 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-red-500 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-0 left-0 w-1 h-full bg-red-500 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-0 right-0 w-1 h-full bg-red-500 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center space-y-8">
        {/* Hacker terminal style header */}
        <div className="space-y-4">
          <div className="text-6xl font-mono animate-pulse">💀</div>
          <div className="text-2xl font-mono text-red-400 animate-pulse">
            [SYCEK-OSINT] INITIALIZING...
          </div>
          <div className="text-sm font-mono text-gray-400">
            TOP SECRET - CLASSIFIED
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-96 mx-auto space-y-4">
          <div className="bg-gray-900 rounded-full h-3 overflow-hidden border border-red-500">
            <div 
              className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <div className="text-sm font-mono text-red-300">
            {Math.round(progress)}% COMPLETE
          </div>
        </div>

        {/* Current step */}
        <div className="space-y-2">
          <div className="text-lg font-mono text-red-400 animate-pulse">
            {steps[currentStep]}
          </div>
          <div className="text-sm font-mono text-gray-400">
            {message}
          </div>
          
          {/* Animated dots */}
          <div className="flex justify-center space-x-1">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>

        {/* Hacker-style status messages */}
        <div className="space-y-2 text-xs font-mono text-gray-400">
          <div className="animate-pulse">[INFO] Connecting to underground networks...</div>
          <div className="animate-pulse" style={{ animationDelay: '0.5s' }}>[WARN] Bypassing security protocols...</div>
          <div className="animate-pulse" style={{ animationDelay: '1s' }}>[SUCCESS] Target acquired...</div>
        </div>

        {/* Glitch effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-red-500/30 animate-ping"></div>
          <div className="absolute top-3/4 right-1/4 w-24 h-24 border border-red-500/30 animate-ping" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-1/4 left-1/3 w-16 h-16 border border-red-500/30 animate-ping" style={{ animationDelay: '2s' }}></div>
        </div>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-4 left-4 text-red-500 font-mono text-xs">
        [SYSTEM] ONLINE
      </div>
      <div className="absolute top-4 right-4 text-red-500 font-mono text-xs">
        [SECURE] ENCRYPTED
      </div>
      <div className="absolute bottom-4 left-4 text-red-500 font-mono text-xs">
        [STATUS] SCANNING
      </div>
      <div className="absolute bottom-4 right-4 text-red-500 font-mono text-xs">
        [MODE] STEALTH
      </div>
    </div>
  )
}
